import * as React from "react";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import {
  Avatar,
  Box,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
} from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Container, IconButton, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useUserContext } from "../../../context/auth/UserContext";
import DashboardSkeleton from "./dashboardSkeleton";
import {
  PoliciesIcon,
  RiskAssessmentIcon,
  RiskManagementIcon,
  IncidentResponsePlanIcon,
  FilesIcon,
} from "../../../assets/CardIcon";
import { useEffect, useState } from "react";
import { getCompanyProfileById } from "../helper/Company_helper";
import AuthContext from "../../../context/auth/AuthContext";
import { companyList } from "../../CompanyProfilePage/modal";
import CompanyListDialogBox from "./CompanyListDialogBox";
import { useCompanyProfileContext } from "../../../context/CompanyProfile/CompanyProfileContext";
import { getLogo } from "../../CompanyProfilePage/helper";

interface DashboardItem {
  id: number;
  buttonText: string;
  footerText: string;
  iconButton: React.JSXElementConstructor<any>;
}

export default function DashboardCard() {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  const authContext = React.useContext(AuthContext);
  const { setCompanyLogo } = useUserContext();
  const { loadUser, isPortalAuthenticated, token } = authContext;
  const {
    userId,
    setCompanyProfileList,
    setCompanyProfileID,
    companyLogo,
    companyProfileID,
    companyProlifeList,
    isAppLoading,
    setIsApploading,
    isSwitchAccountClick,
    setIsSwitchAccountClick,
    setNoCompaniesUnderClient,
    userAccess,
  } = useUserContext();
  const dashboardList = [
    {
      id: 1,
      media: "/static/images/cards/contemplative-reptile.jpg",
      buttonText: "Policies",
      footerText: "Add or edit Company policies",
      iconButton: PoliciesIcon,
    },
    {
      id: 2,
      media: "/static/images/cards/contemplative-reptile.jpg",
      buttonText: "Risk Assessment",
      footerText:
        "Create or edit cybersecurity or supply chain risk assessment items",
      iconButton: RiskAssessmentIcon,
    },
    {
      id: 3,
      media: "/static/images/cards/contemplative-reptile.jpg",
      buttonText: "Risk Management",
      footerText:
        "Create or edit cybersecurity or supply chain risk management plan items",
      iconButton: RiskManagementIcon,
    },
    {
      id: 4,
      media: "/static/images/cards/contemplative-reptile.jpg",
      buttonText: "Incident Response Plan",
      footerText: "Create or edit incident response plan or playbooks",
      iconButton: IncidentResponsePlanIcon,
    },
    {
      id: 5,
      media: "/static/images/cards/contemplative-reptile.jpg",
      buttonText: "Files",
      footerText: "Upload, view and download files",
      iconButton: FilesIcon,
    },
  ];

  // Function to handle card click
  const handleCardClick = (item: DashboardItem) => {
    // console.log("handleCardClick item", item);
    // Navigate based on the buttonText
    if (item.buttonText === "Policies") {
      navigate("/policies");
    } else if (item.buttonText === "Files") {
      navigate("/file-repository");
    } else if (item.buttonText === "Incident Response Plan") {
      navigate("/incident-response-plan");
    } else if (item.buttonText === "Risk Assessment") {
      navigate("/risk-assessment");
    } else if (item.buttonText === "Risk Management") {
      navigate("/risk-management");
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSelectCompany = async (companyProfileID: number) => {
    // Add additional actions after selecting a company
    const resLogo = await getLogo(companyProfileID);
    if (typeof resLogo === "string") {
      localStorage.setItem("companyLogo", resLogo);
      setCompanyLogo(resLogo);
    }
    localStorage.setItem("companyProfileID", String(companyProfileID));
    setCompanyProfileID(companyProfileID);

    setIsApploading(false);
    setIsSwitchAccountClick(false);
    setNoCompaniesUnderClient(false)
    handleCloseDialog();
  };

  useEffect(() => {
    if (companyProfileID) {
      // Store the isApploading status as false in local storage
    }
  }, [companyProfileID]);

  const fetchCompanyList = async () => {
    let res = null
    if (userAccess.roles[0].roleName === "JSIAdmin" || userAccess.roles[0].roleName === "JSIUser") {
      res = await getCompanyProfileById(token);
    } else {
      res = await getCompanyProfileById(token, userId);
    }

    setCompanyProfileList(res);
    if (res.length === 0) {
      setNoCompaniesUnderClient(true)
    }
    else if (res.length > 1) {
      handleOpenDialog();
    } else if (res.length > 0) {
      localStorage.setItem("companyProfileID", String(res[0].companyProfileID));
      const resLogo = await getLogo(res[0].companyProfileID);
      if (typeof resLogo === "string") {
        localStorage.setItem("companyLogo", resLogo);
        setCompanyLogo(resLogo);
      }
      setIsApploading(false);
      setCompanyProfileID(res[0].companyProfileID);
      setIsSwitchAccountClick(false);
    }

  };

  useEffect(() => {
    if (
      (isPortalAuthenticated && !companyProfileID && userAccess) ||
      isSwitchAccountClick
    ) {
      //localStorage.setItem('isApploading', JSON.stringify(true));
      setIsApploading(true);
      // console.log("Stored isApploading:", localStorage.getItem('isApploading'));
      fetchCompanyList();
    }
  }, [
    isPortalAuthenticated,
    companyProfileID,
    isSwitchAccountClick,
    userAccess,
  ]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 4, sm: 8, md: 12 }}
      >
        {isAppLoading ? (
          <DashboardSkeleton />
        ) : (
          dashboardList.map((item) => (
            <Grid item xs={2} sm={4} md={4} key={item.id}>
              <Card
                sx={{
                  width: "100%",
                  height: "100% ",
                  boxShadow: 5,
                  borderRadius: "15px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <CardActions sx={{ width: "100%" }}>
                  {/* <Stack direction="row" justifyContent="space-between"> */}
                  <Grid
                    width="100%"
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <Grid xs={2}>
                      <IconButton onClick={() => handleCardClick(item)}>
                        {React.createElement(item.iconButton)}
                      </IconButton>
                    </Grid>
                    <Grid xs={8}>
                      <Typography
                        onClick={() => handleCardClick(item)}
                        sx={{
                          color: "#292D32",
                          width: "fit-content",
                          fontWeight: 500,
                          textTransform: "none",
                          cursor: "pointer",
                        }}
                      >
                        {item.buttonText}
                      </Typography>
                    </Grid>
                    <Grid xs={2}>
                      <IconButton onClick={() => handleCardClick(item)}>
                        <ChevronRightIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardActions>
                <CardContent>
                  <Typography
                    color="text.secondary"
                    textAlign="center"
                    sx={{ fontWeight: 400 }}
                  >
                    {item.footerText}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
      <CompanyListDialogBox
        open={dialogOpen}
        companies={companyProlifeList} // Pass the list of companies
        onClose={handleCloseDialog}
        onSelect={handleSelectCompany}
      />
    </React.Fragment>
  );
}
